import { useCallback, useLayoutEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Child, getChild } from '../common/child'
import { getCategorySpecifyMasters, SpecifyMasterType  } from '../common/category'
import { useErrorHandle } from '../common/error/errorHandler'
import { notifyMessage, showLoading } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { memberUrl } from '../common/constant/appUrl'
import { OperationId } from '../common/constant/operationLog'
import { Flag, yesNo } from '../common/constant/classification'
import { GetCategoryMstDto, SpecifyMaster } from '../../dataAccess/webApi/dto/categoryMstDto'
import { GetChildrenSymptomDto } from '../../dataAccess/webApi/dto/childrenSymptomDto'
import { executeGetChildrenSymptom, executePutChildrenSymptom } from '../../dataAccess/webApi/dao/childrenSymptomDao'
import { tabValue } from '../member/memberService'
import { translate } from '../../i18n'

interface UrlParams {
  childId: string
}

/** 入力フォームの項目の定義 */
export interface SymptomForm {
  symptomId: number
  flag: string
  addAnswer?: {[key: string]: any } 
}

export interface Inputs {
  childId: string
  detailUpdateDatetime: string | null
  symptomFlag: string | null
  symptom?: SymptomForm[] | null
}

const FORM_DEFAULT_VALUES: any = {
  detailUpdateDatetime: null,
  symptomFlag: null,
  symptom: null
}

const CATEGORY_KEY = 'symptom'
const DEFAULT_PROJECT_ID = '0' // 市民用の項目は事業設ID'0'
const DEFAULT_FACILITY_ID = '0' // 市民用のデータは施設ID'0'で作成する

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const { childId } = useParams<UrlParams>()
  /** カテゴリー情報 */
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  const { addOperationLog } = useOperationLog()

  const formMethods = useForm<Inputs>({
    defaultValues: { childId, ...FORM_DEFAULT_VALUES}
  })
  
  // ちらつき防止のためにuseLayoutEffect使用
  useLayoutEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001, accessData: [{ userIdRegFlag: yesNo.yes, childId }] })
    
    dispatch(
      showLoading(
        errorHandle(async () => {
          const [
            category, 
            child,
            symptomResp
          ] = await Promise.all([
            getCategorySpecifyMasters({projectId: DEFAULT_PROJECT_ID, category: CATEGORY_KEY, masterType: SpecifyMasterType.SYMPTOM}),
            getChild(childId, DEFAULT_FACILITY_ID),
            executeGetChildrenSymptom(childId, DEFAULT_FACILITY_ID)
          ])
          
          setCategoryMst(category)
          const symptom = symptomResp.result
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(child, symptom, category),
          })
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({
        operationId: OperationId.OP_00000037,
        accessData: [{ userIdRegFlag: yesNo.yes, childId: data.childId }],
      })
      
      dispatch(
        showLoading({
          process: errorHandle(async () => {
            const symptom = getRegistSymptom(data)
            const inputData = {
              facilityId: DEFAULT_FACILITY_ID,
              symptomFlag: data.symptomFlag,
              detailUpdateDatetime: data.detailUpdateDatetime ?? "",
              symptom: symptom
            }
            
            const resp = await executePutChildrenSymptom(data.childId, inputData)
            if (resp.resultCode) {
              // 失敗時
              setChildrenResultCode(resp.resultCode)
            } else {
              // 成功時
              dispatch(notifyMessage(translate('memberModifyConfirmationChild.success.completionMessage')))
              history.push(memberUrl.url(), { tabValue: tabValue.child, childId: data.childId })
            }
            
          }),
          isHiddenMain: false,
        })
      )
    }, [addOperationLog])

  return {
    CATEGORY_KEY,
    categoryMst,
    childrenResultCode,
    formMethods,
    onSubmit,
  }
}

const getInitialInputs = (child: Child, symptom: GetChildrenSymptomDto[], categoryMst: GetCategoryMstDto[]) => {
  const inputs = {
    detailUpdateDatetime: child.detailUpdateDatetime,
    symptomFlag: child.symptomFlag,
    symptom: getSymptomForms(symptom, categoryMst)
  }
  return inputs
}

export const getSymptomForms = (symptom: GetChildrenSymptomDto[], categoryMst: GetCategoryMstDto[])=> {
  const targetCategory = categoryMst.find((value) => value.category === CATEGORY_KEY)
  if (targetCategory?.masterData == null) {
    return undefined
  }
  
  const symptomForm: SymptomForm[] = []
  targetCategory.masterData.forEach((opt: SpecifyMaster) => {
    let matchedSymptom
    if (symptom != null) {
      matchedSymptom = symptom.find((mh) => mh.symptomId === opt.id)
    }
    
    if (matchedSymptom == null) {
      symptomForm.push({
        symptomId: opt.id,
        flag: Flag.OFF,
        addAnswer: {}
      })
    } else {
     symptomForm.push({
      symptomId: opt.id,
      flag: Flag.ON,
      addAnswer: matchedSymptom.addAnswer != null
        ? matchedSymptom.addAnswer
        : {}
     })
    }
  })
  
  return symptomForm
}

/**
 * お子さま情報登録APIのINPUT用の既往症情報を取得
 * @param data 入力値
 * @returns お子さま情報登録APIのINPUT用の既往症情報
 */
export const getRegistSymptom = (data: Inputs) => {
  if (data.symptomFlag !== Flag.ON) {
    return undefined
  }
  
  let result: 
  {
    symptomId: number
    addAnswer?: string | null
  }[] = []
  
  if (data.symptom != null) {
    result = data.symptom
      .filter((value) => value.flag === Flag.ON)
      .map((value) => {
        return {
          symptomId: value.symptomId,
          addAnswer: value.addAnswer != null && Object.keys(value.addAnswer).length
            ? JSON.stringify(value.addAnswer)
            : null,
        }
      })
  }
  
  if (result.length === 0) {
    return undefined
  }
  
  return result
}